/**
 * wmStore.js — Reactive window-manager store (SolidJS signals).
 *
 * Owns:
 *   - dashboards (tabs) with embedded widgets
 *   - active dashboard id
 *   - focus stack (z-index ledger)
 *   - per-widget pending settings drafts
 *
 * Persistence:
 *   - Logged-in users → debounced PATCH /api/v2/dashboards/:id/widgets/:wid
 *   - Anonymous users → localStorage `v2.anon.layout.v1`
 */

import { createMemo } from 'solid-js';
import { createStore, produce } from 'solid-js/store';
import api from './apiClient.js';
import toast from './toastStore.js';
import onboarding from './onboardingStore.js';

// ─── Anonymous-tier localStorage persistence ─────────────────────────
const ANON_STORAGE_KEY = 'v2.anon.layout.v1';
const ANON_ICONS_STORAGE_KEY = 'v2.desktop.icons.anon.v1';
const USER_ICONS_STORAGE_PREFIX = 'v2.desktop.icons.user:';
const ACTIVE_DASH_STORAGE_PREFIX = 'redfur:v2:active-dashboard:';
const ENTITY_DETAIL_TYPES = new Set(['item-detail', 'user-detail', 'guild-detail']);
const DEFAULT_ANON_WELCOME = { x: 118, y: 20, w: 420, h: 300 };

// Desktop icon grid constants
const ICON_GRID_W = 94;
const ICON_GRID_H = 100;
const ICON_PAD_X = 14;
const ICON_PAD_Y = 14;

export function snapIconPosition(x, y, existingIcons = [], ignoreId = null) {
  const viewport = _desktopViewportSize();
  const maxCols = Math.max(1, Math.floor((viewport.w - ICON_PAD_X * 2) / ICON_GRID_W));
  const maxRows = Math.max(1, Math.floor((viewport.h - ICON_PAD_Y * 2) / ICON_GRID_H));
  const col0 = Math.max(0, Math.min(maxCols - 1, Math.round((x - ICON_PAD_X) / ICON_GRID_W)));
  const row0 = Math.max(0, Math.min(maxRows - 1, Math.round((y - ICON_PAD_Y) / ICON_GRID_H)));

  const occupied = new Set();
  for (const ic of existingIcons) {
    if (!ic || ic.id === ignoreId) continue;
    const c = Math.round(((Number(ic.x) || ICON_PAD_X) - ICON_PAD_X) / ICON_GRID_W);
    const r = Math.round(((Number(ic.y) || ICON_PAD_Y) - ICON_PAD_Y) / ICON_GRID_H);
    occupied.add(`${c}:${r}`);
  }

  if (!occupied.has(`${col0}:${row0}`)) {
    return {
      x: ICON_PAD_X + col0 * ICON_GRID_W,
      y: ICON_PAD_Y + row0 * ICON_GRID_H,
    };
  }

  for (let dist = 1; dist <= maxCols + maxRows; dist++) {
    for (let dc = -dist; dc <= dist; dc++) {
      for (let dr = -dist; dr <= dist; dr++) {
        if (Math.abs(dc) !== dist && Math.abs(dr) !== dist) continue;
        const c = col0 + dc;
        const r = row0 + dr;
        if (c < 0 || c >= maxCols || r < 0 || r >= maxRows) continue;
        if (!occupied.has(`${c}:${r}`)) {
          return {
            x: ICON_PAD_X + c * ICON_GRID_W,
            y: ICON_PAD_Y + r * ICON_GRID_H,
          };
        }
      }
    }
  }

  return {
    x: ICON_PAD_X + col0 * ICON_GRID_W,
    y: ICON_PAD_Y + row0 * ICON_GRID_H,
  };
}

function _nextFreeIconSlot(existingIcons = []) {
  const viewport = _desktopViewportSize();
  const maxRows = Math.max(1, Math.floor((viewport.h - ICON_PAD_Y * 2) / ICON_GRID_H));
  const maxCols = Math.max(1, Math.floor((viewport.w - ICON_PAD_X * 2) / ICON_GRID_W));
  const occupied = new Set();
  for (const ic of existingIcons) {
    if (!ic) continue;
    const c = Math.round(((Number(ic.x) || ICON_PAD_X) - ICON_PAD_X) / ICON_GRID_W);
    const r = Math.round(((Number(ic.y) || ICON_PAD_Y) - ICON_PAD_Y) / ICON_GRID_H);
    occupied.add(`${c}:${r}`);
  }
  for (let c = 0; c < maxCols; c++) {
    for (let r = 0; r < maxRows; r++) {
      if (!occupied.has(`${c}:${r}`)) {
        return {
          x: ICON_PAD_X + c * ICON_GRID_W,
          y: ICON_PAD_Y + r * ICON_GRID_H,
        };
      }
    }
  }
  return { x: ICON_PAD_X, y: ICON_PAD_Y };
}

export function summarizeIconSettings(type, settings) {
  if (!settings || typeof settings !== 'object') return '';
  const parts = [];
  if (settings.q) parts.push(`"${String(settings.q).slice(0, 14)}"`);
  else if (settings.search) parts.push(`"${String(settings.search).slice(0, 14)}"`);
  else if (settings.name) parts.push(String(settings.name).slice(0, 16));
  else if (settings.tag) parts.push(String(settings.tag).slice(0, 16));
  else if (settings.zone) parts.push(String(settings.zone).slice(0, 14));
  else if (settings.trader) parts.push(String(settings.trader).slice(0, 14));
  else if (settings.guildName) parts.push(String(settings.guildName).slice(0, 14));
  else if (settings.guild && settings.guild !== 'all') parts.push(String(settings.guild).toUpperCase());

  if (settings.quality) parts.push(String(settings.quality));
  if (settings.trait) parts.push(String(settings.trait));
  if (settings.view && type === 'kiosks') parts.push(String(settings.view));
  if (Number(settings.weekOffset) > 0) parts.push(`T-${settings.weekOffset}wk`);
  if (settings.moonPhaseMode === 'lore' && type === 'eso-clock') parts.push('Lore');
  if (settings.glassBorderless) parts.push('Floating');
  else if (settings.glass) parts.push('Glass');
  return parts.slice(0, 2).join(' · ');
}

function _cleanSettingsForIcon(settings) {
  if (!settings || typeof settings !== 'object') return {};
  const clone = { ...settings };
  delete clone._iconId;
  return clone;
}

function _findDesktopShortcut(list, predicate) {
  if (!Array.isArray(list)) return null;
  for (let i = 0; i < list.length; i++) {
    const ic = list[i];
    if (!ic) continue;
    if (ic.kind === 'folder') {
      const items = Array.isArray(ic.items) ? ic.items : [];
      for (let j = 0; j < items.length; j++) {
        if (items[j] && predicate(items[j])) {
          return { kind: 'folder', folderIndex: i, itemIndex: j, folder: ic, item: items[j] };
        }
      }
    } else if (predicate(ic)) {
      return { kind: 'top', index: i, item: ic };
    }
  }
  return null;
}

function _defaultAnonDesktopIcons() {
  const specs = [
    {
      id: 'anon-icon-eso-clock',
      dashId: 'anon',
      widget_type: 'eso-clock',
      title: "Ja-Kha'jay Moons",
      emoji: '🌙',
      settings: { glass: true, glassBorderless: true, glassOpacity: 14, animateMoons: true, showMoons: true, showPhase: true, showCommentary: true },
    },
    {
      id: 'anon-icon-price-check',
      dashId: 'anon',
      widget_type: 'price-check',
      title: 'Price Check',
      emoji: '💰',
      settings: { mode: 'all' },
    },
    {
      id: 'anon-icon-leaderboard',
      dashId: 'anon',
      widget_type: 'leaderboard',
      title: 'Top Traders',
      emoji: '🏆',
      settings: { metric: 'gold', weekOffset: 0 },
    },
    {
      id: 'anon-icon-kiosks',
      dashId: 'anon',
      widget_type: 'kiosks',
      title: 'Tamriel Kiosks',
      emoji: '🏪',
      settings: { view: 'zones', weekOffset: 0 },
    },
    {
      id: 'anon-icon-live-feed',
      dashId: 'anon',
      widget_type: 'live-feed',
      title: 'Live Trade Feed',
      emoji: '📡',
      settings: { showSales: true, showSyncs: true, showRaffle: true },
    },
    {
      id: 'anon-folder-guild-hub',
      dashId: 'anon',
      kind: 'folder',
      title: 'Guild Hub',
      items: [
        {
          id: 'anon-icon-raffle',
          widget_type: 'raffle',
          title: 'Guild Raffle',
          emoji: '🎟️',
          settings: {},
        },
        {
          id: 'anon-icon-guild-stats',
          widget_type: 'guild-stats',
          title: 'Guild Stats',
          emoji: '📊',
          settings: {},
        },
        {
          id: 'anon-icon-claim-tag',
          widget_type: 'claim-tag',
          title: 'Claim @Tag',
          emoji: '🏷️',
          settings: {},
        },
        {
          id: 'anon-icon-kiosks-list',
          widget_type: 'kiosks',
          title: 'Kiosk Directory',
          emoji: '🗺️',
          settings: { view: 'list', weekOffset: 0 },
        },
      ],
    },
  ];
  return specs.map((item, idx) => ({
    ...item,
    x: ICON_PAD_X,
    y: ICON_PAD_Y + idx * ICON_GRID_H,
  }));
}


function _prettyWidgetType(t) {
  const s = String(t || '').trim();
  if (!s || s === 'undefined' || s === 'null') return 'Shortcut';
  return s.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function _resolveIconSpec(spec = {}, catalog = []) {
  const cleanStr = (v) => {
    const s = String(v ?? '').trim();
    return (s && s !== 'undefined' && s !== 'null') ? s : '';
  };
  let wType = cleanStr(spec.widget_type || spec.widgetType || spec.type);
  let meta = wType ? catalog.find(c => c.type === wType) : null;
  if (!meta && spec.emoji) {
    meta = catalog.find(c => !c.launcherHidden && c.emoji === spec.emoji) || catalog.find(c => c.emoji === spec.emoji) || null;
  }
  if (!wType && meta?.type) {
    wType = meta.type;
  }
  const title = cleanStr(spec.title || spec.label) || cleanStr(meta?.title) || _prettyWidgetType(wType);
  const emoji = cleanStr(spec.emoji) || cleanStr(meta?.emoji) || '🪟';
  return {
    widget_type: wType || 'price-check',
    title,
    emoji,
    meta,
  };
}

function _healDesktopIcons(icons, catalog = []) {
  if (!Array.isArray(icons)) return { icons: [], healed: false };
  let healed = false;
  const out = icons.map(ic => {
    if (!ic || typeof ic !== 'object') return ic;
    if (ic.kind === 'folder') {
      const subRes = _healDesktopIcons(ic.items || [], catalog);
      if (subRes.healed) healed = true;
      const folderTitle = (ic.title && ic.title !== 'undefined') ? ic.title : 'App Folder';
      if (folderTitle !== ic.title) healed = true;
      return { ...ic, title: folderTitle, items: subRes.icons };
    }
    const resolved = _resolveIconSpec(ic, catalog);
    if (ic.widget_type !== resolved.widget_type || ic.title !== resolved.title || !ic.title || ic.title === 'undefined') {
      healed = true;
      return {
        ...ic,
        widget_type: resolved.widget_type,
        title: resolved.title,
        emoji: resolved.emoji,
      };
    }
    return ic;
  });
  return { icons: out, healed };
}

function _loadAnonDesktopIcons() {
  try {
    const raw = localStorage.getItem(ANON_ICONS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch { return null; }
}

function _saveAnonDesktopIcons(icons) {
  try {
    localStorage.setItem(ANON_ICONS_STORAGE_KEY, JSON.stringify(icons || []));
  } catch { /* ignore */ }
}

function _loadUserDesktopIconsLocal(viewerId) {
  try {
    if (!viewerId) return null;
    const raw = localStorage.getItem(`${USER_ICONS_STORAGE_PREFIX}${viewerId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch { return null; }
}

let _userIconsSaveTimer = null;
function _saveDesktopIcons(icons) {
  const viewer = state.viewer;
  if (!viewer) {
    _saveAnonDesktopIcons(icons);
    return;
  }
  try {
    localStorage.setItem(`${USER_ICONS_STORAGE_PREFIX}${viewer.id}`, JSON.stringify(icons || []));
  } catch { /* ignore */ }
  clearTimeout(_userIconsSaveTimer);
  _userIconsSaveTimer = setTimeout(async () => {
    try {
      const current = await api.getPrefs().catch(() => ({ prefs: {} }));
      const basePrefs = (current && typeof current.prefs === 'object' && current.prefs) || {};
      await api.putPrefs({ ...basePrefs, desktopIcons: icons || [] });
    } catch (err) {
      console.warn('[wm] desktopIcons prefs sync failed', err);
    }
  }, 500);
}

function _activeDashStorageKey(viewerId) {
  const id = String(viewerId || '').trim();
  return id ? `${ACTIVE_DASH_STORAGE_PREFIX}${id}` : null;
}

function _loadActiveDashboardId(viewerId, dashboards) {
  try {
    const key = _activeDashStorageKey(viewerId);
    if (!key) return null;
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const match = (dashboards || []).find(d => !d._spyTab && String(d.id) === stored);
    return match?.id ?? null;
  } catch { return null; }
}

function _saveActiveDashboardId(viewerId, dashId) {
  try {
    const key = _activeDashStorageKey(viewerId);
    if (key && dashId !== null && dashId !== undefined) localStorage.setItem(key, String(dashId));
  } catch { /* quota / private mode — silent */ }
}

function _hasEntitySetting(widget) {
  const s = widget?.settings || {};
  if (widget?.widget_type === 'item-detail') return !!String(s.name || '').trim();
  if (widget?.widget_type === 'user-detail') return !!String(s.tag || '').trim();
  if (widget?.widget_type === 'guild-detail') return !!(String(s.name || '').trim() || s.guildId);
  return true;
}

function _stripEmptyDetailWidgets(widgets) {
  return (widgets || []).filter(w => !ENTITY_DETAIL_TYPES.has(w.widget_type) || _hasEntitySetting(w));
}

function _loadAnonLayout() {
  try {
    const raw = localStorage.getItem(ANON_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.widgets)) return null;
    return parsed;
  } catch { return null; }
}

function _saveAnonLayout(widgets) {
  try {
    // Strip the welcome-login (always re-injected) and any non-serializable bits.
    // Issue #92 — also strip transient detail-window instances (popup→window
    // promotions). They're session-scoped on purpose and must not bleed into
    // the persisted anon layout.
    const slim = _stripEmptyDetailWidgets(widgets)
      .filter(w => !w._permanent && !w._transient)
      .map(w => ({
        widget_type: w.widget_type,
        x: w.x, y: w.y, w: w.w, h: w.h, z: w.z,
        minimized: !!w.minimized, maximized: !!w.maximized,
        settings: w.settings || {},
      }));
    localStorage.setItem(ANON_STORAGE_KEY, JSON.stringify({ widgets: slim, layoutVersion: 2, savedAt: Date.now() }));
  } catch { /* quota / private mode — silent */ }
}

let _anonSaveTimer = null;
function _saveAnonLayoutDebounced(widgets) {
  clearTimeout(_anonSaveTimer);
  _anonSaveTimer = setTimeout(() => _saveAnonLayout(widgets), 350);
}

// ─── Reactive state ──────────────────────────────────────────────────
const [state, setState] = createStore({
  loaded: false,
  loading: true,
  viewer: null,
  identity: null,
  testingGround: null,
  dashboards: [],          // [{ id, name, emoji, kind, widgets: [...] }]
  activeDashboardId: null,
  focusOrder: [],          // widget ids in z-order, last = top
  catalog: [],             // widget metadata from /api/v2/widgets
  minimizing: [],          // widget ids mid fly-to-taskbar animation (transient)
  desktopIcons: [],        // Windows-style desktop shortcut icons
});

// ─── Per-widget API persist (debounced) ──────────────────────────────
const persistTimers = new Map();
function persistWidgetDebounced(dashId, widget) {
  // Issue #92 — transient detail windows are session-scoped; never push
  // their geometry / settings to the API (the row id isn't real).
  if (widget && widget._transient) return;
  const key = `${dashId}:${widget.id}`;
  clearTimeout(persistTimers.get(key));
  persistTimers.set(key, setTimeout(async () => {
    try {
      await api.updateWidget(dashId, widget.id, {
        x: widget.x, y: widget.y, w: widget.w, h: widget.h, z: widget.z,
        minimized: widget.minimized, maximized: widget.maximized,
        settings: widget.settings,
      });
    } catch (err) { console.warn('[wm] persist failed', err); }
    persistTimers.delete(key);
  }, 350));
}

// ─── Anon widget factory ─────────────────────────────────────────────
let _anonCounter = 0;
function anonWidget(type, x, y, w, h, permanent, extras) {
  _anonCounter += 1;
  return {
    id: `anon-${type}-${_anonCounter}`,
    widget_type: type, x, y, w, h,
    z: extras?.z ?? 0,
    minimized: !!extras?.minimized,
    maximized: !!extras?.maximized,
    settings: extras?.settings || {},
    _iconId: extras?._iconId || extras?.settings?._iconId || null,
    _permanent: !!permanent,
  };
}

// Helper: pull current anon dashboard's widgets, or [] if absent.
function _anonWidgets() {
  return state.dashboards.find(d => d.id === 'anon')?.widgets || [];
}

function _desktopViewportSize() {
  if (typeof document !== 'undefined') {
    const desktop = document.querySelector('.v2-desktop');
    const rect = desktop?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      return { w: rect.width, h: rect.height };
    }
  }
  if (typeof window === 'undefined') return { w: 1200, h: 752 };
  return { w: window.innerWidth, h: Math.max(0, window.innerHeight - 48) };
}

// Default widget set for first-visit anonymous users:
// Leaves a clean left column for Windows-style Desktop Icons, floats the
// ESO Ja-Kha'jay Clock at low transparency in the upper sky so Masser & Secunda
// hover over the dashboard, and arranges Welcome, Price Check, Leaderboard,
// and Live Feed around it.
function _defaultAnonLayout() {
  const viewport = _desktopViewportSize();
  const iconRail = 112; // Reserve left rail for desktop shortcut icons
  const pad = 16;
  const gap = 14;
  const width = Math.max(0, Math.floor(viewport.w));
  const height = Math.max(0, Math.floor(viewport.h));

  const floatingClockSettings = {
    glass: true,
    glassBorderless: true,
    glassOpacity: 14,
    animateMoons: true,
    showMoons: true,
    showPhase: true,
    showDate: true,
    showCommentary: true,
    _iconId: 'anon-icon-eso-clock',
  };

  const welcomeHeight = Math.min(300, Math.max(220, Math.floor((height - pad * 2 - gap) * 0.45)));
  const priceY = pad + welcomeHeight + gap;
  const availablePriceHeight = Math.max(0, height - priceY - pad);
  const priceMinimized = availablePriceHeight < 220;
  const priceHeight = Math.max(240, Math.min(380, availablePriceHeight));

  if (width >= 1220) {
    const leftX = iconRail;
    const leftWidth = Math.min(430, Math.max(350, Math.floor((width - leftX - pad - gap * 2) * 0.33)));
    const middleAndRightWidth = width - leftX - pad - leftWidth - gap * 2;
    const feedWidth = Math.min(370, Math.max(310, Math.floor(middleAndRightWidth * 0.42)));
    const centerWidth = Math.max(360, middleAndRightWidth - feedWidth);
    const centerX = leftX + leftWidth + gap;
    const feedX = centerX + centerWidth + gap;
    const clockHeight = Math.min(300, Math.max(245, Math.floor((height - pad * 2 - gap) * 0.44)));
    const lbY = pad + clockHeight + gap;
    const lbHeight = Math.max(240, height - lbY - pad);
    const mainHeight = Math.max(300, Math.min(680, height - pad * 2));

    return {
      welcome: { x: leftX, y: pad, w: leftWidth, h: welcomeHeight },
      widgets: [
        {
          type: 'eso-clock',
          x: centerX,
          y: pad,
          w: centerWidth,
          h: clockHeight,
          z: 1,
          settings: floatingClockSettings,
          _iconId: 'anon-icon-eso-clock',
        },
        {
          type: 'leaderboard',
          x: centerX,
          y: lbY,
          w: centerWidth,
          h: lbHeight,
          z: 2,
          settings: { _iconId: 'anon-icon-leaderboard' },
          _iconId: 'anon-icon-leaderboard',
        },
        {
          type: 'live-feed',
          x: feedX,
          y: pad,
          w: feedWidth,
          h: mainHeight,
          z: 3,
          settings: { _iconId: 'anon-icon-live-feed' },
          _iconId: 'anon-icon-live-feed',
        },
        {
          type: 'price-check',
          x: leftX,
          y: priceY,
          w: leftWidth,
          h: priceHeight,
          z: 4,
          minimized: priceMinimized,
          settings: { _iconId: 'anon-icon-price-check' },
          _iconId: 'anon-icon-price-check',
        },
      ],
    };
  }

  const leftX = width >= 760 ? iconRail : pad;
  const availableWidth = Math.max(320, width - leftX - pad - gap);
  const leftWidth = Math.min(440, Math.max(300, Math.floor(availableWidth * 0.48)));
  const rightWidth = Math.max(280, availableWidth - leftWidth);
  const rightX = leftX + leftWidth + gap;
  const clockHeight = Math.min(280, Math.max(230, Math.floor((height - pad * 2 - gap) * 0.44)));
  const lbY = pad + clockHeight + gap;
  const lbHeight = Math.max(220, height - lbY - pad);

  return {
    welcome: { x: leftX, y: pad, w: leftWidth, h: welcomeHeight },
    widgets: [
      {
        type: 'eso-clock',
        x: rightX,
        y: pad,
        w: rightWidth,
        h: clockHeight,
        z: 1,
        settings: floatingClockSettings,
        _iconId: 'anon-icon-eso-clock',
      },
      {
        type: 'leaderboard',
        x: rightX,
        y: lbY,
        w: rightWidth,
        h: lbHeight,
        z: 2,
        settings: { _iconId: 'anon-icon-leaderboard' },
        _iconId: 'anon-icon-leaderboard',
      },
      {
        type: 'live-feed',
        x: rightX,
        y: lbY,
        w: rightWidth,
        h: lbHeight,
        z: 3,
        minimized: true,
        settings: { _iconId: 'anon-icon-live-feed' },
        _iconId: 'anon-icon-live-feed',
      },
      {
        type: 'price-check',
        x: leftX,
        y: priceY,
        w: leftWidth,
        h: priceHeight,
        z: 4,
        minimized: priceMinimized,
        settings: { _iconId: 'anon-icon-price-check' },
        _iconId: 'anon-icon-price-check',
      },
    ],
  };
}

function _defaultAnonWidgets(layout) {
  return layout.widgets.map(widget => anonWidget(
    widget.type, widget.x, widget.y, widget.w, widget.h, false, {
      z: widget.z,
      minimized: !!widget.minimized,
      settings: widget.settings || {},
      _iconId: widget._iconId || widget.settings?._iconId || null,
    },
  ));
}

// ─── Window-manager API ──────────────────────────────────────────────
export const wm = {
  state,

  async init() {
    setState('loading', true);
    try {
      const [health, cat] = await Promise.all([
        api.health(), api.widgetCatalog(),
      ]);
      const viewer = health.viewer;
      setState('viewer', viewer);
      setState('identity', health.identity || null);
      setState('testingGround', health.testingGround || null);
      if (typeof document !== 'undefined' && health.identity?.isAdmin) {
        document.body.classList.add('has-spybar');
      }
      if (typeof document !== 'undefined') {
        document.body.classList.toggle(
          'is-impersonating',
          !!health.identity?.impersonating,
        );
      }
      setState('catalog', cat.widgets || []);

      if (viewer) {
        const [{ dashboards }, prefsRes] = await Promise.all([
          api.listDashboards(),
          api.getPrefs().catch(() => ({ prefs: {} })),
        ]);
        const serverIcons = prefsRes?.prefs?.desktopIcons;
        const localIcons = _loadUserDesktopIconsLocal(viewer.id);
        const rawUserIcons = Array.isArray(serverIcons)
          ? serverIcons
          : (Array.isArray(localIcons) ? localIcons : []);
        const healedUser = _healDesktopIcons(rawUserIcons, cat.widgets || []);
        setState('desktopIcons', healedUser.icons);
        if (healedUser.healed) _saveDesktopIcons(healedUser.icons);

        // Issue #305 — three-state welcome detection. Strip welcome-login
        // only when the user is FULLY linked (Discord viewer + ESO tag).
        // Partial-state users (Discord OAuth but no @-tag) keep / receive
        // the widget so it can urge them to link the missing identity.
        const fullyLinked = !!(viewer && health.identity?.tag);
        const cleaned = (dashboards || []).map(d => ({
          ...d,
          widgets: _stripEmptyDetailWidgets(d.widgets || []).filter(w =>
            fullyLinked ? w.widget_type !== 'welcome-login' : true,
          ),
        }));
        if (!fullyLinked && cleaned.length) {
          const first = cleaned[0];
          const hasWelcome = (first.widgets || []).some(
            w => w.widget_type === 'welcome-login'
          );
          if (!hasWelcome) {
            first.widgets = [
              anonWidget('welcome-login', 40, 40, 480, 320, true /* permanent */),
              ...(first.widgets || []),
            ];
          }
        }
        const activeId = _loadActiveDashboardId(viewer.id, cleaned) ?? cleaned[0]?.id ?? null;
        setState('dashboards', cleaned);
        setState('activeDashboardId', activeId);
        const activeDash = cleaned.find(d => d.id === activeId) || cleaned[0];
        const widgets = activeDash?.widgets || [];
        const ordered = widgets.slice().sort((a, b) => (a.z || 0) - (b.z || 0));
        setState('focusOrder', ordered.map(w => w.id));
        const anyRealWidget = widgets.some(w => w.widget_type !== 'welcome-login');
        if (anyRealWidget) onboarding.markComplete();
      } else {
        // Anonymous: synth a single ephemeral dashboard.
        const restored = _loadAnonLayout();
        const isFirstAnonVisit = restored === null;
        const defaultLayout = _defaultAnonLayout();
        let restoredWidgets = _stripEmptyDetailWidgets(restored?.widgets || []).map(w => anonWidget(
          w.widget_type, w.x, w.y, w.w, w.h, false, {
            z: w.z || 0,
            minimized: !!w.minimized,
            maximized: !!w.maximized,
            settings: w.settings || {},
          },
        ));
        // Upgrade legacy v1 default layouts (which had >0 widgets but no eso-clock and no layoutVersion)
        // while leaving explicit empty `{ widgets: [] }` layouts untouched for test harnesses.
        if (
          !isFirstAnonVisit &&
          restoredWidgets.length > 0 &&
          !restored?.layoutVersion &&
          !restoredWidgets.some(w => w.widget_type === 'eso-clock')
        ) {
          restoredWidgets = _defaultAnonWidgets(defaultLayout);
          _saveAnonLayout(restoredWidgets);
        }
        const seeded = isFirstAnonVisit ? _defaultAnonWidgets(defaultLayout) : [];
        const welcome = defaultLayout?.welcome || DEFAULT_ANON_WELCOME;
        const widgets = [
          anonWidget('welcome-login', welcome.x, welcome.y, welcome.w, welcome.h, true /* permanent */),
          ...restoredWidgets,
          ...seeded,
        ];

        let anonIcons = _loadAnonDesktopIcons();
        if (!anonIcons) {
          anonIcons = _defaultAnonDesktopIcons();
          _saveAnonDesktopIcons(anonIcons);
        }
        // Link any matching initial anon widgets to their desktop icons
        const linkedIcons = anonIcons.map(ic => {
          const match = widgets.find(
            w => w._iconId === ic.id || w.settings?._iconId === ic.id || w.widget_type === ic.widget_type
          );
          return match ? { ...ic, linkedWidgetId: match.id } : ic;
        });
        const healedAnon = _healDesktopIcons(linkedIcons, cat.widgets || []);
        setState('desktopIcons', healedAnon.icons);
        if (healedAnon.healed) _saveAnonDesktopIcons(healedAnon.icons);

        if (!isFirstAnonVisit) onboarding.markComplete();
        if (isFirstAnonVisit && seeded.length) {
          _saveAnonLayout([...seeded]);
        }
        setState('dashboards', [{
          id: 'anon',
          name: 'Visitor',
          emoji: '👤',
          kind: 'user',
          widgets,
        }]);
        setState('activeDashboardId', 'anon');
        const ordered = widgets.slice().sort((a, b) => (a.z || 0) - (b.z || 0));
        setState('focusOrder', ordered.map(w => w.id));
      }
    } catch (err) {
      console.error('[wm] init failed', err);
    } finally {
      setState('loading', false);
      setState('loaded', true);
    }
  },

  activeDashboard: createMemo(() =>
    state.dashboards.find(d => d.id === state.activeDashboardId) || null
  ),

  focus(widgetId) {
    setState('focusOrder', (prev) => {
      const next = prev.filter(id => id !== widgetId);
      next.push(widgetId);
      return next;
    });
    // Persist the new top-of-stack z back to the row so reloads remember
    // the stacking order. Cheap path: only mutate the focused widget.
    const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
    if (!dash) return;
    const newZ = state.focusOrder.length;
    setState('dashboards',
      d => d.id === dash.id, 'widgets',
      w => w.id === widgetId,
      produce(w => { w.z = newZ; }),
    );
    if (dash.id === 'anon') {
      _saveAnonLayoutDebounced(_anonWidgets());
    } else if (!dash._spyTab) {
      // Never persist focus changes for spy tabs — widgets are session-only.
      const w = dash.widgets.find(x => x.id === widgetId);
      if (w) persistWidgetDebounced(dash.id, w);
    }
  },

  zOf(widgetId) {
    const idx = state.focusOrder.indexOf(widgetId);
    return idx < 0 ? 0 : idx + 1;
  },

  async addWidget(type, opts = {}) {
    const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
    if (!dash) return null;
    const meta = state.catalog.find(c => c.type === type);
    const def = meta?.sizes?.default || { w: 480, h: 360 };
    const min = meta?.sizes?.min     || { w: 220, h: 140 };
    const w = Math.max(min.w, Number(opts.geomHint?.w) || Number(def.w) || 480);
    const h = Math.max(min.h, Number(opts.geomHint?.h) || Number(def.h) || 360);
    const baseY = (typeof document !== 'undefined' && document.body.classList.contains('has-spybar')) ? 96 : 72;
    const baseX = 116;
    const step = 28;
    const stack = dash.widgets.length;
    const x = Number.isFinite(opts.geomHint?.x) ? opts.geomHint.x : (baseX + (stack * step) % 240);
    const y = Number.isFinite(opts.geomHint?.y) ? opts.geomHint.y : (baseY + (stack * step) % 200);
    const initialSettings = opts.settings ? { ...opts.settings } : {};
    if (opts._iconId) initialSettings._iconId = opts._iconId;

    if (dash.id === 'anon' || dash._spyTab) {
      const local = anonWidget(type, x, y, w, h, false, {
        settings: initialSettings,
        _iconId: opts._iconId || null,
      });
      setState('dashboards', d => d.id === dash.id, 'widgets', ws => [...ws, local]);
      this.focus(local.id);
      if (dash.id === 'anon') _saveAnonLayout(_anonWidgets());
      return local.id;
    }
    try {
      const newWidget = await api.addWidget(dash.id, {
        widget_type: type,
        x, y, w, h,
        settings: initialSettings,
      });
      const safe = {
        ...newWidget,
        widget_type: newWidget?.widget_type || type,
        x: Number.isFinite(newWidget?.x) ? newWidget.x : x,
        y: Number.isFinite(newWidget?.y) ? newWidget.y : y,
        w: Math.max(min.w, Number(newWidget?.w) || w),
        h: Math.max(min.h, Number(newWidget?.h) || h),
        settings: Object.keys(initialSettings).length
          ? { ...(newWidget?.settings || {}), ...initialSettings }
          : (newWidget?.settings || {}),
        _iconId: opts._iconId || newWidget?.settings?._iconId || null,
        minimized: !!newWidget?.minimized,
        maximized: !!newWidget?.maximized,
      };
      setState('dashboards', d => d.id === dash.id, 'widgets', ws => [...ws, safe]);
      this.focus(safe.id);
      if (Object.keys(initialSettings).length) {
        persistWidgetDebounced(dash.id, safe);
      }
      return safe.id;
    } catch (err) {
      console.warn('[wm] addWidget failed', err);
      toast.error('Could not add widget', { detail: err?.message || 'unknown error' });
      return null;
    }
  },

  async closeWidget(widgetId) {
    const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
    if (!dash) return;
    const w = dash.widgets.find(x => x.id === widgetId);
    if (!w || w._permanent) return;
    setState('dashboards', d => d.id === dash.id, 'widgets', ws => ws.filter(x => x.id !== widgetId));
    setState('focusOrder', prev => prev.filter(id => id !== widgetId));
    if (w._transient || dash._spyTab) return; // session-only — nothing to persist
    if (dash.id === 'anon') {
      _saveAnonLayout(_anonWidgets());
    } else {
      try { await api.deleteWidget(dash.id, widgetId); } catch (err) { console.warn('[wm] close persist failed', err); }
    }
  },

  /**
   * Issue #92 — add a transient (session-only) detail widget to the
   * active dashboard. Used by the popup→window affordance on the
   * Item / User / Guild detail modals: same renderer as a pinned
   * instance, but the row is never persisted to the API or anon
   * localStorage and is dropped when the user switches tabs or
   * reloads. Geometry defaults to a roughly-centered window using
   * the catalog's default size for the type.
   */
  addTransientWidget(type, settings, geomHint) {
    const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
    if (!dash) return null;
    const meta = state.catalog.find(c => c.type === type);
    const def = meta?.sizes?.default || { w: 520, h: 440 };
    const min = meta?.sizes?.min     || { w: 320, h: 220 };
    const w = Math.max(min.w, Number(geomHint?.w) || Number(def.w) || 520);
    const h = Math.max(min.h, Number(geomHint?.h) || Number(def.h) || 440);
    let x = Number.isFinite(geomHint?.x) ? geomHint.x : null;
    let y = Number.isFinite(geomHint?.y) ? geomHint.y : null;
    if (x === null || y === null) {
      const viewport = _desktopViewportSize();
      x = Math.max(24, Math.round((viewport.w - w) / 2));
      y = Math.max(60, Math.round((viewport.h - h) / 2) - 40);
      // Tiny cascade so successive opens don't stack on the same pixel.
      const offset = (dash.widgets.filter(w => w._transient).length % 5) * 24;
      x += offset; y += offset;
    }
    _anonCounter += 1;
    const widget = {
      id: `transient-${type}-${_anonCounter}-${Date.now()}`,
      widget_type: type,
      x, y, w, h,
      z: 0,
      minimized: false,
      maximized: false,
      settings: settings || {},
      _transient: true,
    };
    setState('dashboards', d => d.id === dash.id, 'widgets', ws => [...ws, widget]);
    this.focus(widget.id);
    return widget.id;
  },

  toggleMinimize(widgetId) {
    this._mutWidget(widgetId, w => { w.minimized = !w.minimized; if (w.minimized) w.maximized = false; });
  },
  toggleMaximize(widgetId) {
    this._mutWidget(widgetId, w => { w.maximized = !w.maximized; if (w.maximized) w.minimized = false; });
    this.focus(widgetId);
  },
  setGeometry(widgetId, geom) {
    this._mutWidget(widgetId, w => { Object.assign(w, geom); });
  },
  setSettings(widgetId, settings) {
    this._mutWidget(widgetId, w => {
      const keepIconId = w._iconId || w.settings?._iconId;
      w.settings = keepIconId && settings && !settings._iconId
        ? { ...settings, _iconId: keepIconId }
        : settings;
    });
  },

  // #4/#7 — taskbar click target. Restore a minimised window and raise it;
  // if it's already the focused top window, minimise it instead (Windows
  // taskbar toggle behaviour). Otherwise just bring it to the front.
  activateWindow(widgetId) {
    const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
    const w = dash?.widgets.find(x => x.id === widgetId);
    if (!w) return;
    if (w.minimized) {
      this._mutWidget(widgetId, x => { x.minimized = false; });
      this.focus(widgetId);
      return;
    }
    const order = state.focusOrder;
    const isTop = order.length && order[order.length - 1] === widgetId;
    if (isTop) {
      this.requestMinimize(widgetId);
    } else {
      this.focus(widgetId);
    }
  },

  isMinimizing(widgetId) {
    return state.minimizing.includes(widgetId);
  },
  requestMinimize(widgetId) {
    const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
    const w = dash?.widgets.find(x => x.id === widgetId);
    if (!w || w.minimized) return;
    if (state.minimizing.includes(widgetId)) return;
    setState('minimizing', list => [...list, widgetId]);
  },
  endMinimize(widgetId, commit) {
    setState('minimizing', list => list.filter(id => id !== widgetId));
    if (commit) {
      this._mutWidget(widgetId, w => { w.minimized = true; w.maximized = false; });
    }
  },

  snapWindow(widgetId, rect, maximize) {
    if (maximize) {
      this._mutWidget(widgetId, w => { w.maximized = true; w.minimized = false; });
      this.focus(widgetId);
      return;
    }
    if (!rect) return;
    this._mutWidget(widgetId, w => {
      w.maximized = false;
      w.minimized = false;
      w.x = Math.round(rect.x); w.y = Math.round(rect.y);
      w.w = Math.round(rect.w); w.h = Math.round(rect.h);
    });
    this.focus(widgetId);
  },

  _mutWidget(widgetId, mutator) {
    const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
    if (!dash) return;
    setState('dashboards',
      d => d.id === dash.id, 'widgets',
      w => w.id === widgetId,
      produce(mutator),
    );
    const updatedWidget = dash.widgets.find(x => x.id === widgetId);
    if (updatedWidget && !dash._spyTab) {
      // Two-way auto-save: if this window is linked to a desktop shortcut icon
      // (either top-level or inside an Android folder), sync its updated
      // settings and geometry back to the icon automatically.
      const iconId = updatedWidget._iconId || updatedWidget.settings?._iconId;
      const found = _findDesktopShortcut(
        state.desktopIcons,
        ic => (iconId && ic.id === iconId) || ic.linkedWidgetId === widgetId
      );
      if (found) {
        const nextGeom = {
          x: updatedWidget.x,
          y: updatedWidget.y,
          w: updatedWidget.w,
          h: updatedWidget.h,
        };
        const nextSettings = _cleanSettingsForIcon(updatedWidget.settings);
        if (found.kind === 'top') {
          setState('desktopIcons', found.index, produce(ic => {
            ic.settings = nextSettings;
            ic.windowGeom = nextGeom;
            ic.linkedWidgetId = updatedWidget.id;
          }));
        } else if (found.kind === 'folder') {
          setState('desktopIcons', found.folderIndex, 'items', found.itemIndex, produce(ic => {
            ic.settings = nextSettings;
            ic.windowGeom = nextGeom;
            ic.linkedWidgetId = updatedWidget.id;
          }));
        }
        _saveDesktopIcons(state.desktopIcons);
      }
    }
    if (dash.id === 'anon') {
      _saveAnonLayoutDebounced(_anonWidgets());
    } else if (!dash._spyTab) {
      if (updatedWidget) persistWidgetDebounced(dash.id, updatedWidget);
    }
  },

  // ── Desktop Shortcut Icons & Android Folders API ───────────────────────
  activeDesktopIcons() {
    const dashId = state.activeDashboardId;
    if (!dashId) return state.desktopIcons;
    return state.desktopIcons.filter(
      ic => !ic.dashId || String(ic.dashId) === String(dashId)
    );
  },

  isWidgetLinkedToIcon(widget) {
    if (!widget) return false;
    const iconId = widget._iconId || widget.settings?._iconId;
    return !!_findDesktopShortcut(
      state.desktopIcons,
      ic => (iconId && ic.id === iconId) || ic.linkedWidgetId === widget.id
    );
  },

  addDesktopIcon(spec = {}) {
    const dashId = state.activeDashboardId || 'anon';
    const resolved = _resolveIconSpec(spec, state.catalog);
    const activeIcons = this.activeDesktopIcons();
    const pos = (Number.isFinite(spec.x) && Number.isFinite(spec.y))
      ? snapIconPosition(spec.x, spec.y, activeIcons)
      : _nextFreeIconSlot(activeIcons);

    const id = spec.id || `icon-${resolved.widget_type}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const icon = {
      id,
      dashId,
      kind: 'shortcut',
      widget_type: resolved.widget_type,
      title: resolved.title,
      emoji: resolved.emoji,
      x: pos.x,
      y: pos.y,
      settings: _cleanSettingsForIcon(spec.settings || {}),
      windowGeom: spec.windowGeom || null,
      linkedWidgetId: spec.linkedWidgetId || null,
    };
    const next = [...state.desktopIcons, icon];
    setState('desktopIcons', next);
    _saveDesktopIcons(next);
    toast.success(`Added “${icon.title}” shortcut to desktop`, { ttl: 1800 });
    return icon;
  },

  createDesktopFolder(spec = {}) {
    const dashId = state.activeDashboardId || 'anon';
    const activeIcons = this.activeDesktopIcons();
    const pos = (Number.isFinite(spec.x) && Number.isFinite(spec.y))
      ? snapIconPosition(spec.x, spec.y, activeIcons)
      : _nextFreeIconSlot(activeIcons);

    const id = spec.id || `folder-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const folder = {
      id,
      dashId,
      kind: 'folder',
      title: String(spec.title || 'New Folder').trim(),
      x: pos.x,
      y: pos.y,
      items: Array.isArray(spec.items) ? spec.items : [],
    };
    const next = [...state.desktopIcons, folder];
    setState('desktopIcons', next);
    _saveDesktopIcons(next);
    if (!spec.silent) {
      toast.success(`Created folder “${folder.title}”`, { ttl: 1800 });
    }
    return folder;
  },

  mergeIconsIntoFolder(sourceId, targetId) {
    if (!sourceId || !targetId || sourceId === targetId) return null;
    const sourceIdx = state.desktopIcons.findIndex(ic => ic.id === sourceId);
    const targetIdx = state.desktopIcons.findIndex(ic => ic.id === targetId);
    if (sourceIdx === -1 || targetIdx === -1) return null;

    const source = state.desktopIcons[sourceIdx];
    const target = state.desktopIcons[targetIdx];
    if (!source || !target || source.kind === 'folder') return null;

    const cleanShortcut = (sc) => ({
      id: sc.id,
      widget_type: sc.widget_type,
      title: sc.title,
      emoji: sc.emoji,
      settings: _cleanSettingsForIcon(sc.settings || {}),
      windowGeom: sc.windowGeom || null,
      linkedWidgetId: sc.linkedWidgetId || null,
    });

    if (target.kind === 'folder') {
      const updatedItems = [...(target.items || []), cleanShortcut(source)];
      const next = state.desktopIcons
        .map((ic, idx) => (idx === targetIdx ? { ...ic, items: updatedItems } : ic))
        .filter((_, idx) => idx !== sourceIdx);
      setState('desktopIcons', next);
      _saveDesktopIcons(next);
      toast.info(`Added “${source.title}” to “${target.title}”`, { ttl: 1800 });
      return target.id;
    }

    const folderId = `folder-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const folder = {
      id: folderId,
      dashId: target.dashId || state.activeDashboardId || 'anon',
      kind: 'folder',
      title: 'App Folder',
      x: target.x,
      y: target.y,
      items: [cleanShortcut(target), cleanShortcut(source)],
    };
    const next = state.desktopIcons
      .map((ic, idx) => (idx === targetIdx ? folder : ic))
      .filter((_, idx) => idx !== sourceIdx);
    setState('desktopIcons', next);
    _saveDesktopIcons(next);
    toast.success(`Created folder with “${target.title}” & “${source.title}”`, { ttl: 2000 });
    return folderId;
  },

  addShortcutToFolder(folderId, spec = {}) {
    const folderIdx = state.desktopIcons.findIndex(ic => ic.id === folderId && ic.kind === 'folder');
    if (folderIdx === -1) return null;
    const resolved = _resolveIconSpec(spec, state.catalog);
    const shortcut = {
      id: spec.id || `icon-${resolved.widget_type}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      widget_type: resolved.widget_type,
      title: resolved.title,
      emoji: resolved.emoji,
      settings: _cleanSettingsForIcon(spec.settings || {}),
      windowGeom: spec.windowGeom || null,
      linkedWidgetId: spec.linkedWidgetId || null,
    };
    setState('desktopIcons', folderIdx, produce(folder => {
      folder.items = [...(folder.items || []), shortcut];
    }));
    _saveDesktopIcons(state.desktopIcons);
    toast.success(`Added “${shortcut.title}” to “${state.desktopIcons[folderIdx].title}”`, { ttl: 1800 });
    return shortcut;
  },

  extractShortcutFromFolder(folderId, shortcutId, dropPos = null) {
    const folderIdx = state.desktopIcons.findIndex(ic => ic.id === folderId && ic.kind === 'folder');
    if (folderIdx === -1) return null;
    const folder = state.desktopIcons[folderIdx];
    const items = Array.isArray(folder.items) ? folder.items : [];
    const item = items.find(x => x.id === shortcutId);
    if (!item) return null;

    const remainingItems = items.filter(x => x.id !== shortcutId);
    const dashId = folder.dashId || state.activeDashboardId || 'anon';
    const activeIcons = this.activeDesktopIcons().filter(ic =>
      ic.id !== folderId || remainingItems.length > 0
    );
    const pos = (dropPos && Number.isFinite(dropPos.x) && Number.isFinite(dropPos.y))
      ? snapIconPosition(dropPos.x, dropPos.y, activeIcons)
      : _nextFreeIconSlot(activeIcons);

    const topShortcut = {
      ...item,
      dashId,
      kind: 'shortcut',
      x: pos.x,
      y: pos.y,
    };

    const next = [];
    for (const ic of state.desktopIcons) {
      if (ic.id === folderId) {
        if (remainingItems.length > 0) {
          next.push({ ...ic, items: remainingItems });
        }
      } else {
        next.push(ic);
      }
    }
    next.push(topShortcut);
    setState('desktopIcons', next);
    _saveDesktopIcons(next);
    toast.info(`Moved “${item.title}” to desktop`, { ttl: 1600 });
    return topShortcut;
  },

  ungroupDesktopFolder(folderId) {
    const folderIdx = state.desktopIcons.findIndex(ic => ic.id === folderId && ic.kind === 'folder');
    if (folderIdx === -1) return;
    const folder = state.desktopIcons[folderIdx];
    const items = Array.isArray(folder.items) ? folder.items : [];
    const dashId = folder.dashId || state.activeDashboardId || 'anon';
    const next = state.desktopIcons.filter(ic => ic.id !== folderId);

    for (let i = 0; i < items.length; i++) {
      const sub = items[i];
      const activeForDash = next.filter(ic => !ic.dashId || String(ic.dashId) === String(dashId));
      const pos = i === 0
        ? snapIconPosition(folder.x, folder.y, activeForDash)
        : _nextFreeIconSlot(activeForDash);
      next.push({
        ...sub,
        dashId,
        kind: 'shortcut',
        x: pos.x,
        y: pos.y,
      });
    }
    setState('desktopIcons', next);
    _saveDesktopIcons(next);
    toast.info(`Unpacked folder “${folder.title}”`, { ttl: 1600 });
  },

  pinWindowToDesktop(widgetId) {
    const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
    const w = dash?.widgets.find(x => x.id === widgetId);
    if (!w) return null;
    const meta = state.catalog.find(c => c.type === w.widget_type);
    const existingIconId = w._iconId || w.settings?._iconId;
    const found = _findDesktopShortcut(
      state.desktopIcons,
      ic => (existingIconId && ic.id === existingIconId) || ic.linkedWidgetId === w.id
    );

    if (found) {
      const nextGeom = { x: w.x, y: w.y, w: w.w, h: w.h };
      const nextSettings = _cleanSettingsForIcon(w.settings);
      if (found.kind === 'top') {
        setState('desktopIcons', found.index, produce(ic => {
          ic.settings = nextSettings;
          ic.windowGeom = nextGeom;
          ic.linkedWidgetId = w.id;
        }));
      } else if (found.kind === 'folder') {
        setState('desktopIcons', found.folderIndex, 'items', found.itemIndex, produce(ic => {
          ic.settings = nextSettings;
          ic.windowGeom = nextGeom;
          ic.linkedWidgetId = w.id;
        }));
      }
      _saveDesktopIcons(state.desktopIcons);
      toast.info(`Updated desktop shortcut “${found.item.title}”`, { ttl: 1800 });
      return found.item;
    }

    const wType = w.widget_type || w.type;
    const resolved = _resolveIconSpec({ widget_type: wType, title: meta?.title }, state.catalog);
    const filterSummary = summarizeIconSettings(resolved.widget_type, w.settings);
    const baseTitle = resolved.title;
    const defaultTitle = filterSummary
      ? `${baseTitle} (${filterSummary.replace(/"/g, '')})`.slice(0, 36)
      : baseTitle;

    const icon = this.addDesktopIcon({
      widget_type: resolved.widget_type,
      title: defaultTitle,
      emoji: resolved.emoji,
      settings: _cleanSettingsForIcon(w.settings),
      windowGeom: { x: w.x, y: w.y, w: w.w, h: w.h },
      linkedWidgetId: w.id,
    });

    this._mutWidget(w.id, x => {
      x._iconId = icon.id;
      x.settings = { ...(x.settings || {}), _iconId: icon.id };
    });
    return icon;
  },

  async openDesktopIcon(iconId, opts = {}) {
    const found = _findDesktopShortcut(state.desktopIcons, ic => ic.id === iconId);
    if (!found) return null;
    const icon = found.item;
    const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
    if (!dash) return null;

    if (!opts.forceNew) {
      const existing = dash.widgets.find(
        w => w.id === icon.linkedWidgetId || w._iconId === icon.id || w.settings?._iconId === icon.id
      );
      if (existing) {
        if (existing.minimized) {
          this._mutWidget(existing.id, x => { x.minimized = false; });
        }
        this.focus(existing.id);
        return existing.id;
      }
    }

    const newWid = await this.addWidget(icon.widget_type, {
      settings: { ...(icon.settings || {}), _iconId: icon.id },
      geomHint: icon.windowGeom || undefined,
      _iconId: icon.id,
    });
    if (newWid) {
      if (found.kind === 'top') {
        setState('desktopIcons', found.index, 'linkedWidgetId', newWid);
      } else if (found.kind === 'folder') {
        setState('desktopIcons', found.folderIndex, 'items', found.itemIndex, 'linkedWidgetId', newWid);
      }
      _saveDesktopIcons(state.desktopIcons);
    }
    return newWid;
  },

  renameDesktopIcon(iconId, nextTitle) {
    const trimmed = String(nextTitle || '').trim();
    if (!trimmed) return;
    const topIdx = state.desktopIcons.findIndex(ic => ic.id === iconId);
    if (topIdx !== -1) {
      setState('desktopIcons', topIdx, 'title', trimmed);
      _saveDesktopIcons(state.desktopIcons);
      return;
    }
    const found = _findDesktopShortcut(state.desktopIcons, ic => ic.id === iconId);
    if (found && found.kind === 'folder') {
      setState('desktopIcons', found.folderIndex, 'items', found.itemIndex, 'title', trimmed);
      _saveDesktopIcons(state.desktopIcons);
    }
  },

  moveDesktopIcon(iconId, x, y) {
    const idx = state.desktopIcons.findIndex(ic => ic.id === iconId);
    if (idx === -1) return;
    setState('desktopIcons', idx, produce(ic => {
      ic.x = Math.round(x);
      ic.y = Math.round(y);
    }));
    _saveDesktopIcons(state.desktopIcons);
  },

  removeDesktopIcon(iconId) {
    const topIcon = state.desktopIcons.find(ic => ic.id === iconId);
    if (topIcon) {
      const removedIds = new Set([iconId]);
      if (topIcon.kind === 'folder' && Array.isArray(topIcon.items)) {
        for (const sub of topIcon.items) if (sub?.id) removedIds.add(sub.id);
      }
      const next = state.desktopIcons.filter(ic => ic.id !== iconId);
      setState('desktopIcons', next);
      _saveDesktopIcons(next);
      const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
      for (const w of dash?.widgets || []) {
        if (removedIds.has(w._iconId) || removedIds.has(w.settings?._iconId)) {
          this._mutWidget(w.id, x => {
            x._iconId = null;
            if (x.settings && removedIds.has(x.settings._iconId)) {
              const copy = { ...x.settings };
              delete copy._iconId;
              x.settings = copy;
            }
          });
        }
      }
      toast.info(`Removed “${topIcon.title}”`, { ttl: 1500 });
      return;
    }

    const found = _findDesktopShortcut(state.desktopIcons, ic => ic.id === iconId);
    if (found && found.kind === 'folder') {
      const removedTitle = found.item.title;
      setState('desktopIcons', found.folderIndex, produce(folder => {
        folder.items = (folder.items || []).filter(x => x.id !== iconId);
      }));
      _saveDesktopIcons(state.desktopIcons);
      const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
      const linked = dash?.widgets.find(w => w._iconId === iconId || w.settings?._iconId === iconId);
      if (linked) {
        this._mutWidget(linked.id, w => {
          w._iconId = null;
          if (w.settings && w.settings._iconId === iconId) {
            const copy = { ...w.settings };
            delete copy._iconId;
            w.settings = copy;
          }
        });
      }
      toast.info(`Removed “${removedTitle}” from folder`, { ttl: 1500 });
    }
  },

  // ── Spy tab management (#357) ──────────────────────────────────────────
  /**
   * Open a new spy desktop tab for the given target (one at a time).
   * If a spy tab is already open for the same target, just switch to it.
   * If a spy tab for a DIFFERENT target is open, replace it.
   *
   * The spy tab is purely client-side (never persisted to the server or
   * to anon localStorage). Its _spyTab flag gates every persistence path.
   *
   * Admin-only: guard at call site (SpyBar already checks isAdmin()).
   *
   * @param {string|null} targetId  Discord ID (17-19 digits) or null
   * @param {string|null} tag       ESO @-tag (display label)
   */
  openSpyTab(targetId, tag) {
    if (!state.identity?.isAdmin) return;
    const existing = state.dashboards.find(d => d._spyTab);
    if (existing) {
      const sameTarget =
        (existing._spyTarget?.id || '') === (targetId || '') &&
        (existing._spyTarget?.tag || '') === (tag || '');
      if (sameTarget) {
        // Already open for this target — just switch.
        this.setActiveDashboard(existing.id);
        return;
      }
      // Different target: replace the existing spy tab in-place.
      setState('dashboards',
        d => d._spyTab,
        produce(d => {
          const label = tag || targetId || 'Unknown';
          d.name = `Spy: ${label}`;
          d._spyTarget = { id: targetId, tag };
          d.widgets = [];
        }),
      );
      this.setActiveDashboard(existing.id);
      return;
    }
    // No existing spy tab — create one.
    const id = `spy-${Date.now()}`;
    const label = tag || targetId || 'Unknown';
    setState('dashboards', list => [...list, {
      id,
      name: `Spy: ${label}`,
      emoji: '\u{1F575}', // 🕵 — detective emoji
      kind: 'spy',
      _spyTab: true,
      _spyTarget: { id: targetId, tag },
      widgets: [],
    }]);
    this.setActiveDashboard(id);
  },

  /**
   * Close the spy tab and return to the first non-spy dashboard.
   * Idempotent: safe to call when no spy tab exists.
   */
  closeSpyTab() {
    const spyDash = state.dashboards.find(d => d._spyTab);
    if (!spyDash) return;
    const remaining = state.dashboards.filter(d => !d._spyTab);
    setState('dashboards', remaining);
    if (state.activeDashboardId === spyDash.id) {
      const next = remaining[0]?.id ?? null;
      setState('activeDashboardId', next);
      const widgets = remaining[0]?.widgets || [];
      setState('focusOrder',
        widgets.slice().sort((a, b) => (a.z || 0) - (b.z || 0)).map(w => w.id),
      );
    }
  },

  async addDashboard(name, emoji) {
    try {
      const r = await api.createDashboard({ name: name || 'New Tab', emoji: emoji || '🪟' });
      setState('dashboards', list => [...list, { ...r, widgets: [] }]);
      setState('activeDashboardId', r.id);
      _saveActiveDashboardId(state.viewer?.id, r.id);
      toast.success(`Created “${r.name}”`);
    } catch (err) {
      toast.error('Could not create tab', { detail: err?.message || 'unknown error' });
    }
  },
  async renameDashboard(id, patch) {
    if (id === 'anon') return;
    const d = state.dashboards.find(x => x.id === id);
    if (d?._spyTab) return; // spy tabs are not renamed through the normal flow
    try {
      const r = await api.updateDashboard(id, patch);
      setState('dashboards', d => d.id === id, produce(d => { Object.assign(d, r); }));
      toast.success('Tab renamed');
    } catch (err) {
      toast.error('Rename failed', { detail: err?.message || 'unknown error' });
      throw err;
    }
  },
  async deleteDashboard(id) {
    if (id === 'anon') return;
    const d = state.dashboards.find(x => x.id === id);
    if (d?._spyTab) { this.closeSpyTab(); return; } // spy tabs close via closeSpyTab()
    try {
      await api.deleteDashboard(id);
    } catch (err) {
      toast.error('Delete failed', { detail: err?.message || 'unknown error' });
      throw err;
    }
    toast.success('Tab deleted');
    const remaining = state.dashboards.filter(d => d.id !== id);
    setState('dashboards', remaining);
    if (state.activeDashboardId === id) {
      const next = remaining[0]?.id ?? null;
      setState('activeDashboardId', next);
      _saveActiveDashboardId(state.viewer?.id, next);
      const widgets = remaining[0]?.widgets || [];
      setState('focusOrder', widgets.slice().sort((a, b) => (a.z || 0) - (b.z || 0)).map(w => w.id));
    }
  },
  // Persist a new tab order (drag-to-reorder in the HomeMenu Desks tab).
  // `orderedIds` is the desired left-to-right id sequence; we PATCH each
  // row's sort_order to its new index in parallel, then update local state
  // to match. The 'anon' pseudo-tab (when present) is filtered out — it is
  // never persisted.
  async reorderDashboards(orderedIds) {
    const ids = (orderedIds || []).filter(id => id !== 'anon');
    if (!ids.length) return;
    const lookup = new Map(state.dashboards.map(d => [d.id, d]));
    const reordered = ids.map(id => lookup.get(id)).filter(Boolean);
    // Keep any tabs not present in `orderedIds` (e.g. anon) at their current
    // position — append them at the end so we never lose a row.
    for (const d of state.dashboards) {
      if (!ids.includes(d.id)) reordered.push(d);
    }
    setState('dashboards', reordered);
    try {
      await Promise.all(ids.map((id, i) =>
        api.updateDashboard(id, { sort_order: i }).catch(err => {
          console.warn('[wm] reorder persist failed for', id, err);
        }),
      ));
    } catch (err) {
      console.warn('[wm] reorderDashboards failed', err);
      toast.warn('Tab order may not have saved', { detail: err?.message || 'unknown' });
    }
  },

  setActiveDashboard(id) {
    // Issue #92 — transient detail windows are session-scoped to the
    // dashboard they were spawned on. Drop them from the outgoing
    // dashboard so switching tabs doesn't leave stale popups behind.
    const prevId = state.activeDashboardId;
    if (prevId && prevId !== id) {
      const prev = state.dashboards.find(d => d.id === prevId);
      if (prev && prev.widgets.some(w => w._transient)) {
        setState('dashboards', d => d.id === prevId, 'widgets',
          ws => ws.filter(w => !w._transient),
        );
      }
    }
    const dash = state.dashboards.find(d => d.id === id);
    setState('activeDashboardId', id);
    if (dash && !dash._spyTab && dash.id !== 'anon') {
      _saveActiveDashboardId(state.viewer?.id, dash.id);
    }
    const widgets = dash?.widgets || [];
    const ordered = widgets.slice().sort((a, b) => (a.z || 0) - (b.z || 0));
    setState('focusOrder', ordered.map(w => w.id));
  },

  // ---- Bulk arrangement helpers --------------------------------------------
  arrangeTile() {
    const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
    if (!dash) return;
    const visible = dash.widgets.filter(w => !w.minimized);
    if (!visible.length) return;
    const cols = Math.ceil(Math.sqrt(visible.length));
    const rows = Math.ceil(visible.length / cols);
    const PAD = 8;
    const viewport = _desktopViewportSize();
    const W = viewport.w, H = viewport.h;
    const cw = Math.floor((W - PAD * (cols + 1)) / cols);
    const rh = Math.floor((H - PAD * (rows + 1)) / rows);
    const ordered = visible.slice().sort((a, b) => (a.z || 0) - (b.z || 0));
    ordered.forEach((w, i) => {
      const r = Math.floor(i / cols), c = i % cols;
      this._mutWidget(w.id, x => {
        x.maximized = false;
        x.x = PAD + c * (cw + PAD);
        x.y = PAD + r * (rh + PAD);
        x.w = cw; x.h = rh;
      });
    });
  },
  arrangeCascade() {
    const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
    if (!dash) return;
    const visible = dash.widgets.filter(w => !w.minimized);
    if (!visible.length) return;
    const ordered = visible.slice().sort((a, b) => (a.z || 0) - (b.z || 0));
    const STEP = 32;
    const viewport = _desktopViewportSize();
    const W = Math.min(640, Math.floor(viewport.w * 0.55));
    const H = Math.min(440, Math.floor(viewport.h * 0.7));
    ordered.forEach((w, i) => {
      this._mutWidget(w.id, x => {
        x.maximized = false;
        x.x = 24 + i * STEP;
        x.y = 24 + i * STEP;
        x.w = W; x.h = H;
      });
    });
  },
  // Reset the active dashboard back to its server defaults (logged-in users)
  arrangeDesktopIcons() {
    const dashId = state.activeDashboardId || 'anon';
    let idx = 0;
    const viewport = _desktopViewportSize();
    const maxRows = Math.max(1, Math.floor((viewport.h - ICON_PAD_Y * 2) / ICON_GRID_H));
    setState('desktopIcons', produce(list => {
      for (const ic of list) {
        if (!ic.dashId || String(ic.dashId) === String(dashId)) {
          const col = Math.floor(idx / maxRows);
          const row = idx % maxRows;
          ic.x = ICON_PAD_X + col * ICON_GRID_W;
          ic.y = ICON_PAD_Y + row * ICON_GRID_H;
          idx += 1;
        }
      }
    }));
    _saveDesktopIcons(state.desktopIcons);
  },

  async resetLayout() {
    const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
    if (!dash) return;
    if (dash.id === 'anon') {
      const defaultLayout = _defaultAnonLayout();
      const seeded = _defaultAnonWidgets(defaultLayout);
      const welcome = defaultLayout?.welcome || DEFAULT_ANON_WELCOME;
      const widgets = [
        anonWidget('welcome-login', welcome.x, welcome.y, welcome.w, welcome.h, true),
        ...seeded,
      ];
      const defaultIcons = _defaultAnonDesktopIcons().map(ic => {
        const match = widgets.find(w => w._iconId === ic.id || w.widget_type === ic.widget_type);
        return match ? { ...ic, linkedWidgetId: match.id } : ic;
      });
      setState('dashboards', d => d.id === 'anon', 'widgets', widgets);
      setState('desktopIcons', defaultIcons);
      setState('focusOrder', widgets.slice().sort((a, b) => (a.z || 0) - (b.z || 0)).map(w => w.id));
      _saveAnonLayout(seeded);
      _saveAnonDesktopIcons(defaultIcons);
      toast.info('Visitor showcase layout restored');
      return;
    }
    try {
      // Remove every existing widget on the server, then re-fetch to pick up
      // the seeded default set. We delete in parallel; failures of individual
      // rows are logged and the final list state still reflects the truth
      // returned by the listDashboards call.
      const widgets = dash.widgets.slice();
      await Promise.all(widgets.map(w =>
        api.deleteWidget(dash.id, w.id).catch(err => {
          console.warn('[wm] resetLayout: delete failed for', w.id, err);
        }),
      ));
      const { dashboards } = await api.listDashboards();
      setState('dashboards', dashboards || []);
      const fresh = (dashboards || []).find(d => d.id === dash.id);
      const ws = fresh?.widgets || [];
      setState('focusOrder', ws.slice().sort((a, b) => (a.z || 0) - (b.z || 0)).map(w => w.id));
      toast.success('Layout reset to defaults');
    } catch (err) {
      toast.error('Reset failed', { detail: err?.message || 'unknown error' });
    }
  },

  restoreAll() {
    const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
    if (!dash) return;
    dash.widgets.forEach(w => {
      if (w.minimized || w.maximized) {
        this._mutWidget(w.id, x => { x.minimized = false; x.maximized = false; });
      }
    });
  },

  /**
   * Replace the active dashboard's widgets with the supplied template
   * widgets. Used by SpyBar to load the saved spy template into view.
   * Server persistence: deletes existing widgets then re-adds each
   * template widget. Anonymous mode is unsupported.
   */
  async applySpyTemplate(templateWidgets) {
    const dash = state.dashboards.find(d => d.id === state.activeDashboardId);
    if (!dash || dash.id === 'anon') {
      toast.warn('Cannot apply template to anonymous view');
      return;
    }
    // 1. Wipe existing widgets server-side and locally.
    const existing = dash.widgets.slice();
    for (const w of existing) {
      try { await api.deleteWidget(dash.id, w.id); } catch (err) { console.warn('[wm] template wipe failed', err); }
    }
    setState('dashboards', d => d.id === dash.id, 'widgets', []);
    // 2. Insert each template widget.
    for (const t of templateWidgets) {
      try {
        const created = await api.addWidget(dash.id, {
          widget_type: t.widget_type,
          x: +t.x || 0, y: +t.y || 0,
          w: +t.w || 480, h: +t.h || 360,
          z: +t.z || 0,
          minimized: !!t.minimized,
          maximized: !!t.maximized,
          settings: t.settings || {},
        });
        setState('dashboards', d => d.id === dash.id, 'widgets', ws => [...ws, created]);
      } catch (err) { console.warn('[wm] template insert failed', err); }
    }
    const ordered = (state.dashboards.find(d => d.id === dash.id)?.widgets || [])
      .slice().sort((a, b) => (a.z || 0) - (b.z || 0));
    setState('focusOrder', ordered.map(w => w.id));
  },
};

export default wm;
